import React, { useEffect, useState } from 'react';
import { Flex, Popconfirm } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

// Search and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const VendorsTable = () => {

  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Frontend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [allVendors, setAllVendors] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllVendors = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllVendors(searchTerm);
      if (response.status === 200) {
        const data = response.data;
        setAllVendors(data.results || []);
        setTotalRows(data.count || data.results?.length || 0);
      }
    } catch (e) {
      console.error('Error fetching vendors:', e);
    }
    setLoading(false);
  }

  // Frontend pagination logic
  const paginateData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allVendors.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  }

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  }


  useEffect(() => {
    fetchAllVendors();
  }, [searchTerm]);

  // Apply frontend pagination when data or page changes
  useEffect(() => {
    paginateData();
  }, [allVendors, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const response = await actions.deleteVendors(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchAllVendors();
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }

  const defaultColumns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      width: 220,
    },
    {
      title: 'CONTACT EMAIL',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a?.email?.localeCompare(b?.email),
      width: 220,
    },
    {
      title: 'ADDRESS',
      dataIndex: 'address',
      key: 'address',
      width: 220,
    },
    {
      title: 'GST NUMBER',
      dataIndex: 'gst_number',
      key: 'gst_number',
      width: 220,
      sorter: (a, b) => a?.gst_number?.localeCompare(b?.gst_number),
    },
    {
      title: 'MOBILE',
      dataIndex: 'phone',
      key: 'phone',
      width: 220,
      sorter: (a, b) => a?.phone?.localeCompare(b?.phone),
    },
    {
      title: 'WEBSITE',
      dataIndex: 'website',
      key: 'website',
      width: 220,
      sorter: (a, b) => a?.website?.localeCompare(b?.website),
    },
    {
      title: 'BALANCE',
      dataIndex: 'balance',
      key: 'balance',
      width: 220,
      sorter: (a, b) => a?.balance - b?.balance,
    },
    {
      title: 'PAID TO DATE',
      dataIndex: 'paid_date',
      key: 'paid_date',
      width: 220,
      sorter: (a, b) => a?.paid_date?.localeCompare(b?.paid_date),
    },
    {
      title: 'CREATED DATE',
      dataIndex: 'create_date',
      key: 'create_date',
      width: 220,
      sorter: (a, b) => a?.create_date?.localeCompare(b?.create_date),
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      width: 220,
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <a>
              <EditOutlined
                onClick={() => navigate(`/vendors/edit/${record.id}/`)}
                style={{
                  verticalAlign: 'middle',
                }}
              />
            </a>
            <Popconfirm title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null
      }
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  return (
    <div style={{ padding: 10 }}>
      <Flex gap='large' justify='space-between'>
        <Flex gap='large' align='center' wrap>
          <div>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} vendors
          </div>

          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search vendors"
            onRefresh={() => fetchAllVendors()}
            refreshing={loading}
            addLabel="Add Vendor"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/vendors/new")}
          />
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
            rowSelection={{
              type: selectionType,
              ...rowSelection,
            }}
            loading={loading}
            bordered
            size="small"
            dataSource={dataSource}
            columns={columns}
            rowKey={row => row.id}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRows,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                }
              },
            }}
            scroll={{
                x: "max-content", // Horizontal scroll
                y: "calc(100vh - 350px)", // Vertical scroll
              }}
              sticky
          />
      </div>
    </div>
  );
};
export default VendorsTable;
